import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { CheckIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  Link,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";

import { useForgotPasswordMutation } from "redux/services/apiService";
import { useGetConfigsQuery } from "redux/services/config";

import { InferType, object, string } from "yup";

import LoginHero from "./LoginHero";

/* =======================
 * Schema
 * ======================= */
const schema = object({
  email: string().email("Invalid email format").required("Email is required"),
}).defined();

export type ForgotPasswordFormData = InferType<typeof schema>;

type ViewState = "form" | "success";

const pad2 = (n: number) => String(n).padStart(2, "0");

const ForgotPassword: React.FC = (): JSX.Element => {
  const { t } = useTranslation("common");
  const toast = useToast();
  const navigate = useNavigate();
  const { data: config } = useGetConfigsQuery();
  const [forgotPassword, { isLoading: isSubmittingApi }] = useForgotPasswordMutation();

  const [view, setView] = useState<ViewState>("form");
  const [sentEmail, setSentEmail] = useState<string>("");
  const [secondsLeft, setSecondsLeft] = useState<number>(60);

  // supaya interval gak double
  const timerRef = useRef<number | null>(null);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ForgotPasswordFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { email: "" },
  });

  const logoUrl = useMemo(() => {
    return config?.data?.[0]?.site_logo
      ? `${process.env.REACT_APP_API_URL}/${config.data[0].site_logo}`
      : "";
  }, [config]);

  const startCountdown = () => {
    // reset dulu
    setSecondsLeft(60);

    // clear interval lama
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          // stop di 0
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // cleanup saat unmount
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const onSubmit = async (values: ForgotPasswordFormData) => {
    try {
      await forgotPassword({ email: values.email }).unwrap();

      toast({
        title: t("reset_link_sent"),
        description: t("check_your_email"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSentEmail(values.email);
      setView("success");
      startCountdown();
    } catch (error: any) {
      // toast({
      //   title: t("failed"),
      //   description: error?.data?.message || t("something_went_wrong"),
      //   status: "error",
      //   duration: 4000,
      //   isClosable: true,
      // });
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleResend = async () => {
    if (!sentEmail) return;

    try {
      await forgotPassword({ email: sentEmail }).unwrap();
      toast({
        title: t("reset_link_sent"),
        description: t("check_your_email"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      startCountdown();
    } catch (error: any) {
      toast({
        title: t("failed"),
        description: error?.data?.message || t("something_went_wrong"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const mmss = `00:${pad2(secondsLeft)}`;

  return (
    <Flex minH="100vh" bg="white">
      {/* LEFT */}
      <Flex
        flex="1"
        direction="column"
        px={{ base: 6, md: 10, lg: 16 }}
        py={{ base: 8, md: 10 }}
      >
        {/* Logo */}
        <Box mb={{ base: 10, md: 14 }}>
          {logoUrl ? (
            <Image src={logoUrl} maxH="44px" objectFit="contain" />
          ) : (
            <Box h="44px" />
          )}
        </Box>

        {/* Content center */}
        <Flex flex="1" align="center" justify="center">
          <Box w="full" maxW="520px">
            {view === "form" ? (
              <>
                <Stack spacing={3} mb={6} textAlign="center">
                  <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="gray.800">
                    {t("forgot_password")}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {t("forgot_password_desc")}
                  </Text>
                </Stack>

                <Box
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="12px"
                  boxShadow="sm"
                  p={{ base: 5, md: 6 }}
                >
                  <Stack spacing={4}>
                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel fontSize="sm" color="gray.700">
                        {t("email")}
                      </FormLabel>
                      <Input
                        placeholder={t("email_placeholder")}
                        h="44px"
                        borderRadius="10px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "red.400", boxShadow: "none" }}
                        {...register("email", {
                          required: t("email_required") as string,
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: t("email_invalid") as string,
                          },
                        })}
                      />
                      <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                    </FormControl>

                    <Button
                      colorScheme="brand"
                      isLoading={isSubmitting || isSubmittingApi}
                      onClick={handleSubmit(onSubmit)}
                      h="44px"
                      borderRadius="10px"
                      isDisabled={!isValid}
                      _disabled={{
                        bg: "gray.200",
                        color: "gray.500",
                        cursor: "not-allowed",
                        _hover: {
                          bg: "gray.200",
                        },
                      }}
                    >
                      {t("send_reset_link")}
                    </Button>

                    <Flex justify="flex-end">
                      <Link
                        as={RouterLink}
                        to="/login"
                        fontSize="sm"
                        color="gray.600"
                        _hover={{ textDecoration: "underline" }}
                      >
                        {t("back_to_login")}
                      </Link>
                    </Flex>
                  </Stack>
                </Box>
              </>
            ) : (
              // ===== SUCCESS VIEW =====
              <Center flexDirection="column" textAlign="center" gap={4}>
                <Center
                  w="48px"
                  h="48px"
                  bg="green.500"
                  borderRadius="full"
                  color="white"
                >
                  <CheckIcon boxSize={6} />
                </Center>

                <Stack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    {t("fp_success_title")}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {t("fp_success_desc")}{" "}
                    <Text as="span" fontWeight="semibold" color="gray.700">
                      {sentEmail}
                    </Text>
                  </Text>
                </Stack>

                <Button
                  variant="outline"
                  borderColor="red.400"
                  color="red.500"
                  _hover={{ bg: "red.50" }}
                  _active={{ bg: "red.100" }}
                  w="full"
                  maxW="380px"
                  h="44px"
                  borderRadius="10px"
                  onClick={handleBackToLogin}
                >
                  {t("fp_back_to_login")}
                </Button>

                {/* Countdown / Resend */}
                {secondsLeft > 0 ? (
                  <Text fontSize="sm" color="gray.500" m={0}>
                    {t("fp_no_email")}{" "}
                    <Text as="span" color="red.500" fontWeight="semibold" m={0}>
                      {t("fp_resend_in", { time: mmss })}
                    </Text>
                  </Text>
                ) : (
                  <HStack spacing={1} fontSize="sm">
                    <Text color="gray.500" m={0}>{t("fp_no_email")}</Text>
                    <Link
                      color="red.500"
                      fontWeight="semibold"
                      onClick={handleResend}
                      _hover={{ textDecoration: "underline" }}
                    >
                      {t("fp_resend_now")}
                    </Link>
                  </HStack>
                )}
              </Center>
            )}
          </Box>
        </Flex>
      </Flex>

      {/* RIGHT */}
      <LoginHero />
    </Flex>
  );
};

export default ForgotPassword;
